import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Link as MLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import apiService from '../services/api';
import { API_ENDPOINTS } from '../utils/apiUrls';

const IMPACT_COLORS = {
  high: '#d32f2f',
  medium: '#f9a825',
  low: '#388e3c',
};

const getImpactChipStyles = (impact) => {
  const key = impact?.toLowerCase?.() ?? '';
  const baseColor = IMPACT_COLORS[key] || '#757575';
  return {
    bgcolor: `${baseColor}1A`,
    color: baseColor,
    fontWeight: 600,
    textTransform: 'capitalize',
  };
};

const toApiDate = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
};

const EconomicCalendarPreview = ({
  date,
  limit = 5,
  offset = 0,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const targetDate = useMemo(() => toApiDate(date), [date]);

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get(
        API_ENDPOINTS.ECONOMIC_CALENDAR.GET_ALL,
        {
          date: targetDate,
          limit,
          offset,
        },
      );
      const items = Array.isArray(response) ? response : response?.items ?? [];
      setEvents(items.slice(0, limit));
    } catch (err) {
      console.error('Economic calendar fetch error:', err);
      setEvents([]);
      setError('Economic calendar data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [limit, offset, targetDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          p: 2,
          border: {
            xs: 'none',
            sm: '1px solid #e0e0e0',
          },
          borderRadius: {
            xs: 0,
            sm: 2,
          },
          bgcolor: {
            xs: 'white',
            sm: '#f5f5f5',
          },
          mb: 2,
          boxSizing: 'border-box',
        }}
      >
        <Stack
          direction="row"
          justifyContent={{ xs: 'center', sm: 'space-between' }}
          alignItems={{ xs: 'center', sm: 'flex-start' }}
          spacing={1}
          flexWrap="wrap"
        >
          <Box textAlign={{ xs: 'center', sm: 'left' }}>
            <Typography variant="h6" fontWeight="bold">
              <MLink
                component={RouterLink}
                to="/calendar"
                underline="hover"
                color="inherit"
              >
                Economic Calendar
              </MLink>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {dayjs(targetDate).format('MMMM D, YYYY')}
            </Typography>
          </Box>

          {!isMobile && (
            <MLink
              component={RouterLink}
              to="/calendar"
              underline="always"
              color="inherit"
            >
              See more
            </MLink>
          )}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography
          variant="body2"
          color="error"
          textAlign="center"
          sx={{ px: 2 }}
        >
          {error}
        </Typography>
      ) : events.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ px: 2 }}
        >
          No scheduled events for this date.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {events.map((event) => (
            <Box
              key={event.id || `${event.date}-${event.event}`}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                p: 1.5,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1.5}
              >
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mb: 0.5 }}
                  >
                    {event.event}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {event.country || '—'} • {event.time || 'All Day'}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={event.impact || 'N/A'}
                  sx={getImpactChipStyles(event.impact)}
                />
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 2 }}
                mt={1.5}
              >
                <Typography variant="caption" color="text.secondary">
                  Forecast: {hasValue(event.forecast) ? event.forecast : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Previous: {hasValue(event.previous) ? event.previous : '—'}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {isMobile && (
        <Box
          mt={2}
          textAlign="center"
          sx={{
            pb: { xs: 6, sm: 0 },
          }}
        >
          <MLink
            component={RouterLink}
            to="/calendar"
            underline="always"
            color="inherit"
          >
            See more
          </MLink>
        </Box>
      )}
    </Box>
  );
};

export default EconomicCalendarPreview;
